import { of } from 'rxjs'
import {
  ResourceStatus,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { ResourceType, ResourceVariant, getResourceTypeDetails } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'

export function buildStatusResources(): ViewableApplicationResource[] {
  const resourceTypes = [
    ResourceType.ec2Instance,
    ResourceType.rdsDbInstance,
    ResourceType.s3Bucket,
    ResourceType.lambdaFunction,
    ResourceType.dynamoDbTable,
    ResourceType.ecsService,
    ResourceType.eksCluster,
    ResourceType.elbLoadBalancer,
    ResourceType.secretsManagerSecret,
  ]

  const statuses = Object.values(ResourceStatus).filter((v) => !isNaN(Number(v))) as ResourceStatus[]

  return statuses.map((stat, index) => {
    const rt = resourceTypes[index % resourceTypes.length]
    return {
      resourceType: rt,
      resourceVariant: null,
      resourceArn: `arn:aws:MOCK-TEST:us-east-1:123456789012:resource/${index}` as Arn,
      sourceResourceName: `source-${ResourceStatus[stat].toLowerCase()}`,
      targetResourceName: `target-${ResourceStatus[stat].toLowerCase()}`,
      resourceProperties: [],
      status: stat,
      progress: 0.2,
      message: `Status: ${ResourceStatus[stat]}`,
      issues: new Set(),
      displayResourceType: getResourceTypeDisplayString(rt, null),
      resourceTypeDetails: getResourceTypeDetails({ resourceType: rt }),
    } as ViewableApplicationResource
  })
}

// -------------------------------------------------------------------------
// Parent–child resource group scenarios for visual verification
//
// Each scenario is a top-level parent (cluster / DB cluster / ASG) plus children
// linked by parentArn. They mirror the _resolveParentNeedsAttention unit tests so
// the "Needs attention" rollup vs. the parent's own status pill can be verified
// visually without provisioning real resources. Rendered in BOTH Recovery and
// Backup contexts on /test_page — the trigger sets differ (UNSATISFIED_DEPENDENCY
// is recovery-only), so scenario 4b diverges between the two tables.
// -------------------------------------------------------------------------

interface MockResourceSpec {
  type: ResourceType
  variant?: ResourceVariant
  status: ResourceStatus
  // Children link to this resource via parentArn. Build the tree top-down.
  children?: MockResourceSpec[]
}

function flattenScenario(
  spec: MockResourceSpec,
  prefix: string,
  parentArn: Arn | null,
  out: ViewableApplicationResource[]
): void {
  const variant = spec.variant ?? null
  const arn = `arn:MOCK-TEST:parent-child:${prefix}` as Arn
  const isTrigger =
    spec.status === ResourceStatus.FAILED ||
    spec.status === ResourceStatus.NOT_PROCESSED ||
    spec.status === ResourceStatus.UNSATISFIED_DEPENDENCY ||
    spec.status === ResourceStatus.SCAN_INFECTED ||
    spec.status === ResourceStatus.SCAN_FAILED
  out.push({
    resourceType: spec.type,
    resourceVariant: variant,
    resourceArn: arn,
    parentArn: parentArn ?? undefined,
    sourceResourceName: prefix,
    targetResourceName: prefix,
    resourceProperties: [],
    status: spec.status,
    progress: 0.4,
    // hasIssues drives the per-row issue icon and the parent's issue-count rollup.
    hasIssues: isTrigger,
    message: `Status: ${ResourceStatus[spec.status]}`,
    issues: new Set(),
    displayResourceType: getResourceTypeDisplayString(spec.type, variant as ResourceVariant),
    resourceTypeDetails: getResourceTypeDetails({
      resourceType: spec.type,
      resourceVariant: variant as ResourceVariant,
    }),
  } as ViewableApplicationResource)
  spec.children?.forEach((child, i) => flattenScenario(child, `${prefix}-${i}`, arn, out))
}

export function buildParentChildScenarioResources(): ViewableApplicationResource[] {
  const aks = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.azureAksCluster,
    status,
    children,
  })
  const eks = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.eksCluster,
    status,
    children,
  })
  const ns = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.k8sResource,
    variant: ResourceVariant.k8sNamespace,
    status,
    children,
  })
  const deploy = (status: ResourceStatus): MockResourceSpec => ({
    type: ResourceType.k8sResource,
    variant: ResourceVariant.k8sDeployment,
    status,
  })
  const nodePool = (status: ResourceStatus, children?: MockResourceSpec[]): MockResourceSpec => ({
    type: ResourceType.azureAksNodePool,
    status,
    children,
  })
  const dbInstance = (status: ResourceStatus): MockResourceSpec => ({
    type: ResourceType.rdsDbInstance,
    status,
  })
  const ec2 = (status: ResourceStatus): MockResourceSpec => ({ type: ResourceType.ec2Instance, status })

  // [scenarioPrefix, tree] — prefix becomes the source/target name so rows self-describe.
  const scenarios: [string, MockResourceSpec][] = [
    // 1. THE BUG: AKS cluster itself FAILED, children skipped (NOT_PROCESSED). Multi-level chain.
    //    Expect: parent shows "Failed", NOT "Needs attention".
    [
      's1-aks-cluster-failed',
      aks(ResourceStatus.FAILED, [
        nodePool(ResourceStatus.NOT_PROCESSED, [
          ns(ResourceStatus.NOT_PROCESSED, [
            deploy(ResourceStatus.NOT_PROCESSED),
            deploy(ResourceStatus.NOT_PROCESSED),
          ]),
        ]),
      ]),
    ],
    // 2. Healthy EKS parent with one failed child. Expect: "Needs attention" + issue icon.
    [
      's2-eks-healthy-failed-child',
      eks(ResourceStatus.SUCCEEDED, [
        ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED), deploy(ResourceStatus.SUCCEEDED)]),
      ]),
    ],
    // 3. In-progress EKS parent with a failed + pending child. Expect: "Needs attention".
    [
      's3-eks-inprogress-failed-child',
      eks(ResourceStatus.PROCESSING, [
        ns(ResourceStatus.PROCESSING, [deploy(ResourceStatus.FAILED), deploy(ResourceStatus.PENDING)]),
      ]),
    ],
    // 4a. Parent itself NOT_PROCESSED (a trigger state, not just FAILED). Expect: own "Not Processed" pill.
    [
      's4a-aks-not-processed',
      aks(ResourceStatus.NOT_PROCESSED, [ns(ResourceStatus.NOT_PROCESSED, [deploy(ResourceStatus.NOT_PROCESSED)])]),
    ],
    // 4b. Parent UNSATISFIED_DEPENDENCY — a RECOVERY-only trigger. Expect: own pill in Recovery,
    //     but "Needs attention" in Backup (where it isn't a trigger). Side-by-side divergence.
    [
      's4b-eks-unsatisfied-dep',
      eks(ResourceStatus.UNSATISFIED_DEPENDENCY, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED)])]),
    ],
    // 5. Parent NO_STATUS (not a trigger) with a failed child. Expect: "Needs attention" in both.
    [
      's5-eks-no-status',
      eks(ResourceStatus.NO_STATUS, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.FAILED)])]),
    ],
    // 6. Parent SCAN_INFECTED with a SCAN_FAILED child. Expect: own pill in both (trigger in each set).
    [
      's6-aks-scan-infected',
      aks(ResourceStatus.SCAN_INFECTED, [ns(ResourceStatus.SUCCEEDED, [deploy(ResourceStatus.SCAN_FAILED)])]),
    ],
    // 7. RDS Aurora cluster, all children healthy. Expect: own Success pill, no rollup, no issue icon.
    [
      's7-aurora-all-healthy',
      {
        type: ResourceType.rdsDbCluster,
        status: ResourceStatus.SUCCEEDED,
        children: [dbInstance(ResourceStatus.SUCCEEDED), dbInstance(ResourceStatus.SUCCEEDED)],
      },
    ],
    // 8. Settled EKS parent with in-flight children. Expect: parent promoted to "Restoring"
    //    (_displayStatus), no rollup since PROCESSING/PENDING aren't trigger states.
    [
      's8-eks-settled-children-inflight',
      eks(ResourceStatus.SUCCEEDED, [
        ns(ResourceStatus.PROCESSING, [deploy(ResourceStatus.PROCESSING), deploy(ResourceStatus.PENDING)]),
      ]),
    ],
    // Extra: Auto Scaling Group → EC2 Instances (common non-K8s group), one instance failed.
    //        Expect: "Needs attention" on the healthy ASG.
    [
      's9-asg-failed-instance',
      {
        type: ResourceType.autoScalingGroup,
        status: ResourceStatus.SUCCEEDED,
        children: [ec2(ResourceStatus.FAILED), ec2(ResourceStatus.SUCCEEDED)],
      },
    ],
  ]

  const out: ViewableApplicationResource[] = []
  for (const [prefix, tree] of scenarios) {
    flattenScenario(tree, prefix, null, out)
  }
  return out
}

// -------------------------------------------------------------------------
// Mock Application factory for the test page modals showcase
// -------------------------------------------------------------------------
